// Lib
import { chunk, assign, uniq } from 'lodash';
import { get, isEmpty } from 'lodash/fp';

// Utils
import globalLogger from '../../../logger';
import { http } from '../../../utils/services/http';
import { asyncResource } from '../../../utils/services/http/asyncResource/asyncResource';
import { ResourceTypes } from '../../../utils/services/http/asyncResource/asyncResourceConstants';
import { getTokenElementBatches, TokenGroup } from '../../../utils/services/http/elementRequestTokenUtils';
import { createDebouncedActionDispatcher } from '../../../utils/services/reduxActionDispatcherUtils';

const logger = globalLogger.createChannel('element.comments-service');

// Selectors
import { getAllComments } from './commentsSelector';

// Actions
import { commentsLoad } from './commentActions';
import { getTokenElementIdGroupsThunk } from '../../../utils/permissions/permissionsActions';

// Types
import { GenericReduxThunkAction } from '../../../../common/actions/actionTypes';
import { MNCommentMap } from '../../../../common/comments/commentTypes';
import { TIMES } from '../../../../common/utils/timeUtil';

export const fetchComments =
    ({ commentIds }: { commentIds: string[] }): GenericReduxThunkAction =>
    async (dispatch, getState): Promise<MNCommentMap> => {
        const state = getState();
        const commentsState = getAllComments(state);

        // Don't fetch a comment we already have
        const commentIdsToFetch = commentIds.filter((commentId) => !commentsState[commentId]);

        if (isEmpty(commentIdsToFetch)) return {};

        const commentIdBatches = chunk(commentIdsToFetch, 200);

        return Promise.all(commentIdBatches.map((ids) => http({ url: 'comments', params: { ids: ids.join(',') } })))
            .then((responses) => {
                let commentsMap = {};

                responses.forEach((response) => {
                    const comments = get(['data', 'comments'], response);
                    commentsMap = assign(commentsMap, comments);
                });

                if (isEmpty(commentsMap)) return {};

                dispatch(commentsLoad({ comments: commentsMap }));

                return commentsMap;
            })
            .catch((err) => {
                // Sensitive data audit: SAFE — warn/error auto-sanitized (comment text/author fields redacted; error normalized)
                // Context: Passing IDs is acceptable (opaque UUIDs); sanitizer redacts content-like keys from error payloads.
                logger.error('Failed to retrieve comments', err, { commentIds });
                return {};
            });
    };

type FetchCommentThreadCommentsArgs = {
    commentThreads: { threadId: string; elementId: string }[];
};

/**
 * Fetches the comments for the given comment threads.
 */
export const fetchCommentThreadComments =
    (args: FetchCommentThreadCommentsArgs): GenericReduxThunkAction =>
    async (dispatch): Promise<MNCommentMap> =>
        dispatch(
            asyncResource(
                ResourceTypes.commentThreads,
                args.commentThreads.map((thread) => thread.threadId),
            )(async (threadIdsToFetch) => {
                if (isEmpty(threadIdsToFetch)) return {};

                const commentThreads = args.commentThreads.filter((thread) =>
                    threadIdsToFetch!.includes(thread.threadId),
                );
                const elementIds = uniq(commentThreads.map((thread) => thread.elementId));
                const elementIdToThreadIdsMap = commentThreads.reduce(
                    (acc, thread) => {
                        acc[thread.elementId] = acc[thread.elementId] || [];
                        acc[thread.elementId].push(thread.threadId);
                        return acc;
                    },
                    {} as Record<string, string[]>,
                );

                const tokenGroups = (await dispatch(
                    getTokenElementIdGroupsThunk({ elementIds, permissionIdsOverride: undefined }),
                )) as unknown as TokenGroup[];
                const batches = getTokenElementBatches(tokenGroups);

                const responses = await Promise.all(
                    batches.map((batch) => {
                        const { elementIds, token } = batch;
                        const threadIds = uniq(elementIds.flatMap((elementId) => elementIdToThreadIdsMap[elementId]));

                        return http({
                            url: 'comments/threads',
                            timeout: 30 * TIMES.SECOND,
                            params: {
                                ids: threadIds.join(','),
                                elementIds: batch.elementIds.join(','),
                                tokens: token,
                            },
                        });
                    }),
                );

                const commentsMap = responses.reduce((acc, response) => {
                    const { data = {} } = response;
                    const { comments = {} } = data;
                    return {
                        ...acc,
                        ...comments,
                    };
                }, {} as MNCommentMap);

                dispatch(commentsLoad({ comments: commentsMap }));

                return commentsMap;
            }),
        ) as unknown as Promise<MNCommentMap>;

/**
 * Waits a debounce time before performing the fetchCommentThreadComments action.
 * Collates the comment threads to fetch during the debounce period.
 */
export const debouncedFetchCommentThreadComments = createDebouncedActionDispatcher<
    MNCommentMap,
    Parameters<typeof fetchCommentThreadComments>
>(fetchCommentThreadComments);
